import React, { useEffect, useState } from "react";
import { CiFilter } from "react-icons/ci";
import BuildingEntryForm from "./components/Forms/buildingform";
import FilterBuildingModal from "./components/Modals/FilterBuildingModal";
import ListHeader from "../../../../components/ListHeader";
import {
  BuildingStatusesMapping,
  BuildingTypesMapping,
  DispatchTypesMapping,
} from "./config/formDataConfig";
import Pagination from "@mui/material/Pagination";
import { ColorRing } from "react-loader-spinner";

import RowsPerPageSelector from "../../../../components/Tables/Pagination/RowsPerPageSelector";
import { useAuth } from "../../../../contexts/AuthContext";
import API_BASE_URL from "../../../../Constants";
import ViewBuildingModal from "./components/Modals/ViewBuildingModal";

const BuildingListFields = [
  { key: "salesRep", label: "Sales Rep ID" },
  { key: "buildingReference", label: "Building Site" },
  { key: "location", label: "Location" },
  { key: "customerID", label: "Customer ID" },
  { key: "agencyID", label: "Management ID" },
  { key: "buildingType", label: "Building Type" },
  { key: "dispatchType", label: "Dispatch Type" },
  { key: "buildingStatus", label: "Status" },
];

const BuildingList = () => {
  const { currentUser } = useAuth();
  // const [buildings, setBuildings] = useState([]);
  const [filteredBuildings, setFilteredBuildings] = useState([]);
  const [viewMode, setViewMode] = useState("table");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isViewOpen, setIsViewOpen] = useState(false);
  // const [selectedBuildingID, setSelectedBuildingID] = useState(null);
  const [selectedBuilding, setSelectedBuilding] = useState(null);
  const [selectedBuildingIndex, setSelectedBuildingIndex] = useState(-1);
  const [isLoading, setIsLoading] = useState(false);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const rowsPerPage = currentUser?.rowsPerPage ?? 25;

  const [sortConfig, setSortConfig] = useState({ key: null, direction: null });

  useEffect(() => {
    const sortedBuildings = [...filteredBuildings].sort((a, b) => {
      const aValue = a[sortConfig.key] || "";
      const bValue = b[sortConfig.key] || "";

      // Handle "" values explicitly
      if (aValue === "" && bValue !== "") {
        return sortConfig.direction === "ascending" ? 1 : -1; // a comes last in ascending, first in descending
      }
      if (bValue === "" && aValue !== "") {
        return sortConfig.direction === "ascending" ? -1 : 1; // b comes last in ascending, first in descending
      }
      if (aValue === "" && bValue === "") {
        return 0; // both are "", equal
      }

      const aIsNumber = !isNaN(Number(aValue));
      const bIsNumber = !isNaN(Number(bValue));

      if (aIsNumber && bIsNumber) {
        // Both are numbers, compare numerically
        return sortConfig.direction === "ascending"
          ? Number(aValue) - Number(bValue)
          : Number(bValue) - Number(aValue);
      } else if (aIsNumber && !bIsNumber) {
        // a is a number, b is a string
        return sortConfig.direction === "ascending" ? -1 : 1;
      } else if (!aIsNumber && bIsNumber) {
        // a is a string, b is a number
        return sortConfig.direction === "ascending" ? 1 : -1;
      } else {
        // Both are strings, compare lexicographically
        if (aValue < bValue) {
          return sortConfig.direction === "ascending" ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === "ascending" ? 1 : -1;
        }
        return 0;
      }
    });

    setFilteredBuildings(sortedBuildings);
  }, [sortConfig]);

  const handleSort = (key) => {
    let direction = "ascending";
    if (sortConfig.key === key && sortConfig.direction === "ascending") {
      direction = "descending";
    }
    setSortConfig({ key, direction });
  };

  const fetchBuildings = async () => {
    try {
      setIsLoading(true);

      // Validate page and size parameters
      const validPage = isNaN(currentPage) || currentPage < 1 ? 1 : currentPage;
      const validSize =
        isNaN(rowsPerPage) || rowsPerPage < 1 ? 10 : rowsPerPage;

      const queryString = searchQuery
        ? `&q=${encodeURIComponent(searchQuery)}`
        : "";
      const response = await fetch(
        `${API_BASE_URL}/api/setup/buildings?page=${
          validPage - 1
        }&size=${validSize}${queryString}`,
        { credentials: "include" }
      );

      if (!response.ok) throw new Error("Network response was not ok");

      const data = await response.json();
      setFilteredBuildings(Array.isArray(data.content) ? data.content : []);

      setCurrentPage(data.number + 1);
      setTotalPages(data.totalPages);
    } catch (error) {
      console.error("Fetch error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchBuildings();
  }, [currentPage, rowsPerPage]);

  const handleSearch = () => {
    setCurrentPage(1);
    fetchBuildings(1, rowsPerPage, searchQuery);
  };

  const handleOpenCreateModal = () => setIsCreateOpen(true);
  const handleCloseCreateModal = () => setIsCreateOpen(false);

  const handleOpenViewModal = (building, index) => {
    setSelectedBuildingIndex(index);
    setSelectedBuilding(building);
    setIsViewOpen(true);
  };

  const handleCloseViewModal = () => {
    setSelectedBuilding(null);
    setSelectedBuildingIndex(0);
    setIsViewOpen(false);
  };

  const refreshBuildings = () => {
    fetchBuildings(currentPage, rowsPerPage, searchQuery);
  };

  const handlePrev = () => {
    if (selectedBuildingIndex > 0) {
      const prevIndex = selectedBuildingIndex - 1;
      const prevBuilding = filteredBuildings[prevIndex];
      setSelectedBuilding(prevBuilding);
      setSelectedBuildingIndex(prevIndex);
    }
  };

  const handleNext = () => {
    if (selectedBuildingIndex < filteredBuildings.length - 1) {
      const nextIndex = selectedBuildingIndex + 1;
      const nextBuilding = filteredBuildings[nextIndex];
      setSelectedBuilding(nextBuilding);
      setSelectedBuildingIndex(nextIndex);
    }
  };

  // Utility function to map values
  const mapFieldValue = (fieldKey, value) => {
    switch (fieldKey) {
      case "buildingStatus":
        return BuildingStatusesMapping[value] || "N/A";
      case "buildingType":
        return BuildingTypesMapping[value] || "N/A";
      case "dispatchType":
        return DispatchTypesMapping[value] || "N/A";
      default:
        return value || "N/A";
    }
  };

  const hasPrev = selectedBuildingIndex > 0;
  const hasNext = selectedBuildingIndex < filteredBuildings.length - 1;

  const toggleFilterModal = () => setIsFilterModalOpen(!isFilterModalOpen);

  return (
    <div className="mx-auto pl-4 pr-4 h-screen w-full">
      {/* List Header */}
      <ListHeader
        title="Building List"
        searchQuery={searchQuery}
        handleSearch={handleSearch}
        handleSearchChange={(e) => setSearchQuery(e.target.value)}
        viewMode={viewMode}
        handleViewModeChange={setViewMode}
        handleOpenCreateModal={handleOpenCreateModal}
      >
        {/* Filter Button */}
        <button
          className="py-2 px-3 border-sss-blue-sel rounded-lg flex border text-sss-blue-sel font-bold hover:bg-sss-blue hover:text-white transition duration-300 ease-in-out"
          onClick={toggleFilterModal}
        >
          <CiFilter className="text-2xl" /> Filter
        </button>
      </ListHeader>

      {/* Loading Indicator */}
      {isLoading && (
        <div className="flex justify-center items-center">
          <ColorRing
            visible={isLoading}
            height="200"
            width="200"
            ariaLabel="color-ring-loading"
            wrapperStyle={{}}
            wrapperClass="color-ring-wrapper"
            colors={["#033B5C", "#033B5C", "#033B5C", "#033B5C", "#033B5C"]}
          />
        </div>
      )}

      {/* Table View */}
      {viewMode === "table" && !isLoading && (
        <div className="max-h-[500px] overflow-y-auto border border-gray-300">
          {" "}
          {/* Scrollable container */}
          <table className="min-w-full bg-white rounded-lg shadow-lg border-2 border-gray-200">
            <thead className="bg-gray-200 sticky top-0 z-10">
              <tr>
                <th
                  key={"actions"}
                  className="px-4 py-2 text-left font-bold text-sss-blue-sel"
                ></th>
                {BuildingListFields.map((field) => (
                  <th
                    key={field.key}
                    className="px-4 py-2 text-left font-bold text-sss-blue-sel cursor-pointer"
                    onClick={() => handleSort(field.key)}
                  >
                    {field.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="text-gray-700">
              {filteredBuildings.map((building, index) => (
                <tr key={building.buildingID} className="border-t">
                  <td className="px-4 py-2">
                    <button
                      onClick={() => handleOpenViewModal(building, index)}
                      className="text-sss-info font-semibold hover:underline"
                    >
                      View
                    </button>
                  </td>
                  {BuildingListFields.map((field) => (
                    <td key={field.key} className="px-4 py-2">
                      {mapFieldValue(field.key, building[field.key]) || "N/A"}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Card View */}
      {viewMode === "card" && !isLoading && (
        <div className="grid grid-cols-1 sm:grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4 p-4">
          {filteredBuildings.map((building, index) => (
            <div
              key={building.buildingID}
              className="p-4 bg-white rounded-r-2xl shadow-md border-2 border-gray-200 flex"
            >
              {/* Placeholder Image Section */}
              <div className="w-1/4 flex items-center justify-center">
                <img
                  src="https://via.placeholder.com/150"
                  alt="Building Placeholder"
                  className="rounded-l-lg object-cover w-full h-full"
                />
              </div>

              {/* Building Data Section */}
              <div className="w-2/4 pl-4 space-y-3 bg-gray-100 p-6 border">
                <p>
                  <strong>ID:</strong> {building.buildingID}
                </p>
                <h3 className="font-bold text-lg mb-2">
                  {building.buildingReference || "Building Site"}
                </h3>
                <p>
                  <strong>Location:</strong> {building.location || "N/A"}
                </p>
                <p>
                  <strong>Status:</strong>{" "}
                  {BuildingStatusesMapping[building.buildingStatus] || "N/A"}
                </p>
                <p>
                  <strong>Type:</strong>{" "}
                  {BuildingTypesMapping[building.buildingType] || "N/A"}
                </p>
              </div>

              {/* Action Buttons Section */}
              <div className="w-1/4 flex flex-col items-center justify-center space-y-1 pb-4 border-t border-b border-r rounded-r-xl">
                <button
                  onClick={() => handleOpenViewModal(building, index)}
                  className="bg-transparent text-sss-blue-sel hover:font-semibold hover:cursor-pointer w-1/2 py-2"
                >
                  View
                </button>
                <button className="text-gray-600 w-1/2 py-2 rounded cursor-not-allowed">
                  Edit
                </button>
                <button className="text-gray-600 w-1/2 py-2 rounded cursor-not-allowed">
                  Actions
                </button>
                <button className="text-gray-600 w-1/2 py-2 rounded cursor-not-allowed">
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      <div className="flex justify-between items-center p-4 border-t border-gray-300 bg-white">
        <Pagination
          count={totalPages}
          page={currentPage}
          onChange={(event, value) => setCurrentPage(value)}
          color="primary"
          shape="rounded"
        />
        <div className="flex items-center">
          <RowsPerPageSelector />
        </div>
      </div>

      {/* Filter Modal */}
      <FilterBuildingModal
        open={isFilterModalOpen}
        handleClose={toggleFilterModal}
      />

      {/* Building Entry Form Modal */}
      <BuildingEntryForm
        open={isCreateOpen}
        onClose={handleCloseCreateModal}
        refreshBuildings={refreshBuildings} // Pass the function
      />

      {/* View Building Modal */}
      {isViewOpen && (
        <ViewBuildingModal
          open={isViewOpen}
          // id={selectedBuildingID}
          selectedBuildingData={selectedBuilding}
          onClose={handleCloseViewModal}
          setPrev={handlePrev}
          setNext={handleNext}
          hasPrev={hasPrev}
          hasNext={hasNext}
          refreshBuildings={refreshBuildings} // Pass the function
        />
      )}
    </div>
  );
};

export default BuildingList;
